import { useState } from 'react';
import PayPalButton from '../../components/PayPalButton';

type PlanKey = 'pro' | 'basic' | 'pro_early' | 'basic_annual' | 'pro_annual';

export default function UpgradeButtons({ proOnly = false, earlyOffer = false }: { proOnly?: boolean; earlyOffer?: boolean }) {
    const [showPayPal, setShowPayPal] = useState<PlanKey | null>(null);
    const [cycle, setCycle] = useState<'monthly' | 'annual'>('monthly');

    const proKey:   PlanKey = cycle === 'annual' ? 'pro_annual'   : 'pro';
    const basicKey: PlanKey = cycle === 'annual' ? 'basic_annual' : 'basic';

    return (
        <div className="flex flex-col gap-2 mt-1">

            {/* Toggle mensual / anual */}
            <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1 self-start mb-1">
                <button
                    onClick={() => { setCycle('monthly'); setShowPayPal(null); }}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-all ${cycle === 'monthly' ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-slate-200'}`}
                >
                    Mensual
                </button>
                <button
                    onClick={() => { setCycle('annual'); setShowPayPal(null); }}
                    className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md transition-all ${cycle === 'annual' ? 'bg-primary text-white' : 'text-slate-400 hover:text-slate-200'}`}
                >
                    Anual
                    <span className="bg-brand-accent/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">-2 meses</span>
                </button>
            </div>

            {/* Oferta de activación temprana — solo durante el trial (siempre mensual) */}
            {earlyOffer && (
                showPayPal === 'pro_early' ? (
                    <div className="p-3 rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-orange-500/10">
                        <p className="text-xs font-bold text-amber-300 mb-0.5">🔥 Activación preferencial</p>
                        <p className="text-[11px] text-amber-200/70 mb-2">Plan Pro completo por $149/mes · precio exclusivo durante tu prueba</p>
                        <PayPalButton plan="pro_early" />
                        <button onClick={() => setShowPayPal(null)} className="mt-1 text-[11px] text-slate-400 hover:text-slate-300 w-full text-center">Cancelar</button>
                    </div>
                ) : (
                    <button
                        onClick={() => setShowPayPal('pro_early')}
                        className="flex items-center justify-between gap-3 p-3 rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/10 to-orange-500/10 hover:from-amber-500/20 hover:to-orange-500/20 transition-all"
                    >
                        <div className="flex items-center gap-2">
                            <span className="text-[18px]">🔥</span>
                            <div className="text-left">
                                <p className="text-xs font-bold text-amber-300">Activación preferencial — $149/mes</p>
                                <p className="text-[11px] text-amber-200/60">Plan Pro completo · precio exclusivo durante tu prueba</p>
                            </div>
                        </div>
                        <span className="text-xs font-bold text-amber-300 whitespace-nowrap">Activar →</span>
                    </button>
                )
            )}

            {/* Plan Pro */}
            {showPayPal === proKey ? (
                <div className="p-3 rounded-xl bg-gradient-to-r from-primary/20 to-primary/20 border border-primary/30">
                    <p className="text-xs font-bold text-white mb-2">
                        {cycle === 'annual' ? 'Plan Pro Anual — $149/mes · $1,788/año' : 'Plan Pro — $179/mes'}
                    </p>
                    <PayPalButton plan={proKey} />
                    <button onClick={() => setShowPayPal(null)} className="mt-1 text-[11px] text-slate-400 hover:text-slate-300 w-full text-center">Cancelar</button>
                </div>
            ) : (
                <button
                    onClick={() => setShowPayPal(proKey)}
                    className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gradient-to-r from-primary/90 to-primary hover:from-primary hover:to-primary transition-all text-white shadow-lg shadow-primary/25"
                >
                    <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[18px]">workspace_premium</span>
                        <div className="text-left">
                            {cycle === 'annual' ? (
                                <>
                                    <p className="text-xs font-bold">Plan Pro Anual — $149/mes</p>
                                    <p className="text-[11px] opacity-80">$1,788/año · ahorras $360 vs mensual</p>
                                </>
                            ) : (
                                <>
                                    <p className="text-xs font-bold">Plan Pro — $179/mes</p>
                                    <p className="text-[11px] opacity-80">Seguimiento, reactivación y más</p>
                                </>
                            )}
                        </div>
                    </div>
                    <span className="text-xs font-bold">Suscribirse →</span>
                </button>
            )}

            {/* Plan Básico */}
            {!proOnly && (
                showPayPal === basicKey ? (
                    <div className="p-3 rounded-xl bg-slate-800 border border-slate-700">
                        <p className="text-xs font-bold text-white mb-2">
                            {cycle === 'annual' ? 'Plan Básico Anual — $83/mes · $996/año' : 'Plan Básico — $99/mes'}
                        </p>
                        <PayPalButton plan={basicKey} />
                        <button onClick={() => setShowPayPal(null)} className="mt-1 text-[11px] text-slate-400 hover:text-slate-300 w-full text-center">Cancelar</button>
                    </div>
                ) : (
                    <button
                        onClick={() => setShowPayPal(basicKey)}
                        className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
                    >
                        <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-slate-500 text-[18px]">check_circle</span>
                            <div className="text-left">
                                {cycle === 'annual' ? (
                                    <>
                                        <p className="text-xs font-bold text-slate-700 dark:text-slate-200">Plan Básico Anual — $83/mes</p>
                                        <p className="text-[11px] text-slate-500">$996/año · ahorras $192 vs mensual</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-xs font-bold text-slate-700 dark:text-slate-200">Plan Básico — $99/mes</p>
                                        <p className="text-[11px] text-slate-500">Atención y agenda automática</p>
                                    </>
                                )}
                            </div>
                        </div>
                        <span className="text-xs font-bold text-slate-500 dark:text-slate-400">Suscribirse →</span>
                    </button>
                )
            )}
        </div>
    );
}
